import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import {
  getCodeData,
  getInitFormData,
  getInitSheet,
  parseDate,
} from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import SnSheet from "@shared/components/Grid/SnSheet";
import { globalInfo, menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { IconButton, Tooltip } from "@mui/material";

interface Props {
  Menuprops: menuInfo;
  PkFields: string[]; // PK 필드 목록
  tableName: string; // 저장할 테이블 이름
  controllerName: string; // 저장할 API 컨트롤러 이름
  cdOpList?: Array<{ code: string; name: string }>; // cd_op 콤보박스 데이터
  cd_biz: string;
  userInfo: globalInfo["userInfo"];
}

const SN_PRD_BAS_REG_044_POP_001 = forwardRef(
  (
    {
      Menuprops,
      PkFields,
      tableName,
      controllerName,
      cdOpList,
      cd_biz,
      userInfo,
    }: Props,
    ref
  ) => {
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any>();
    const gridref1 = useRef<any>(null);

    const validationCacheRef = useRef<{
      tpBomList: Array<{ code: string; name: string }>;
    }>({ tpBomList: [] });

    //#region grid 길이 관련
    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    // 공백 제거 + 대소문자 무시 비교용 정규화
    const norm = (v: any) =>
      String(v ?? "")
        .trim()
        .toUpperCase();

    // [{code,name}] → {norm(code):code, norm(name):code} 맵 생성
    const makeCodeMap = (list: Array<{ code: string; name: string }>) => {
      const m = new Map<string, string>();
      (list ?? []).forEach((x) => {
        if (!x) return;
        const code = String(x.code ?? "");
        const name = String(x.name ?? "");
        if (code) m.set(norm(code), code);
        if (name) m.set(norm(name), code);
      });
      return m;
    };

    // 입력값을 “코드”로 변환(없으면 기본값/원본 유지)
    const toCode = (
      input: any,
      codeMap: Map<string, string>,
      fallback?: string
    ) => {
      const key = norm(input);
      if (!key) return fallback ?? "";
      return codeMap.get(key) ?? fallback ?? String(input ?? "");
    };

    //#endregion

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitSheet({});

          // 오류내용 칼럼 수동 추가
          rtn.grid.sheet1.push({
            cd_column: "error_msg",
            nm_column: "오류내용",
            qt_width: 200,
            tp_column: "text",
            fg_visible: "Y",
            yn_required: "N",
            dc_editcon: "1 == 1",
            editable: false, // 수정 불가
          });

          setColums(rtn.grid.sheet1);

          gridref1.current.resetGridData();
        } catch (error) {}
      };
      fetchform();
    }, []);

    // useEffect(() => {
    //   if (gridref1.current) {
    //     gridref1.current.setCodeDataList(codeDataList); //  SnSheet 내부에서 코드 데이터 갱신 메서드 실행
    //   }
    // }, [codeDataList]); //  codeDataList가 변경될 때마다 실행

    // 한글 주석: 행 자체를 지우지 않고 선택한 행의 값만 비운다(빈 행 = KeyField만 남김).
    // 선택된 행이 여러 개면 모두, 없으면 포커스된 셀의 행을 대상으로 한다.
    const onDelete = () => {
      const api = gridref1.current?.api;
      if (!api) return;

      let nodes: any[] = api.getSelectedNodes?.() ?? [];
      if (nodes.length === 0) {
        const focusedCell = api.getFocusedCell();
        const node =
          focusedCell != null
            ? api.getDisplayedRowAtIndex(focusedCell.rowIndex)
            : null;
        if (node) nodes = [node];
      }
      if (nodes.length === 0) return;

      nodes.forEach((node) => {
        node.setData({ KeyField: node.data?.KeyField });
      });
    };

    const normalizeDate = (val: string) => val?.replace(/[-/]/g, "");

    const onSave = async (): Promise<boolean> => {
      try {
        const api = gridref1.current?.api;
        if (!api) return false;

        const isValid = await Validation();
        if (!isValid) {
          return false;
        }

        // 2) 대상 노드 수집(빈행/0레벨 제외)

        const nodes: any[] = [];

        api.forEachNode((n: any) => {
          const row = n.data;

          if (isEmptyRow(n.data)) return;

          if (Number(row.no_level ?? 0) === 0) return;
          nodes.push(n);
        });

        if (nodes.length === 0) {
          await showDialog("alter", "저장할 데이터가 없습니다.");
          return false;
        }

        // 3) 코드 맵 준비(검증과 동일 기준) — tp_bom은 Validation에서 가져온 리스트 재사용 가능

        const tpBomList = validationCacheRef.current.tpBomList.length
          ? validationCacheRef.current.tpBomList
          : await getCodeData("PRD002", "");
        const opComboList = cdOpList ?? [];

        const tpBomMap = makeCodeMap(tpBomList);
        const opMap = makeCodeMap(opComboList);

        // 4) 기본값 준비
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const todayStr = `${yyyy}${mm}${dd}`;
        const dateRegex = /^\d{8}$/;
        const normDate = (v: any) => String(v ?? "").replace(/[-/]/g, "");

        // 5) 두 테이블 생성
        const boml: any[] = [];
        const bomhByItem = new Map<string, any>();

        for (const node of nodes) {
          const r = node.data;

          const cd_item = String(r.cd_item ?? "");
          const cd_matl = String(r.cd_matl ?? "");
          const no_line = Number(r.no_line ?? 0);

          // 저장 시점에 값 주입/보정
          // 코드값 정규화 (code/name 둘 다 허용)
          const cd_op = toCode(r.cd_op, opMap, ""); // 없으면 빈값
          const tp_bom = toCode(r.tp_bom, tpBomMap, "STD"); // 없으면 STD

          // 날짜 기본값 + 형식 보정
          let dt_start = normDate(r.dt_start);
          if (!dt_start || !dateRegex.test(dt_start)) dt_start = todayStr;

          let dt_end = normDate(r.dt_end);
          if (!dt_end || !dateRegex.test(dt_end)) dt_end = "21001231";

          // 수치 기본값
          const qt_just = isNaN(Number(r.qt_just)) ? 1 : Number(r.qt_just);
          const rt_loss = isNaN(Number(r.rt_loss)) ? 0 : Number(r.rt_loss);
          const qt_need = isNaN(Number(r.qt_need)) ? 1 : Number(r.qt_need);

          // 사업장
          const _cd_biz = cd_biz ?? userInfo?.cd_biz ?? "";

          // ─ prd_boml (디테일)
          boml.push({
            ...r,
            cd_firm: userInfo.cd_firm,
            cd_biz: _cd_biz,
            cd_item,
            cd_matl,
            cd_op,
            tp_bom,
            dt_start,
            dt_end,
            no_line,
            rt_loss,
            qt_just,
            qt_need,
          });

          // ─ prd_bomh (헤더) : cd_item distinct
          if (cd_item && !bomhByItem.has(cd_item)) {
            bomhByItem.set(cd_item, {
              cd_firm: userInfo.cd_firm,
              cd_biz: _cd_biz,
              cd_item,
              qt_unit: 1,
              cd_emp: userInfo.cd_emp,
            });
          }
        }

        const bomh = Array.from(bomhByItem.values());

        // 6) 저장 페이로드 (요청 형식)
        const SaveData = {
          prd_bomh: {
            insert: bomh,
            modifiedRowcount: bomh.length,
          },
          prd_boml: {
            insert: boml,
            modifiedRowcount: boml.length,
          },
        };
        const ok = await fnSave(controllerName, SaveData);
        if (!ok) return false;

        gridref1.current.resetUpdate();
        gridref1.current.resetGridData();
        return true;
      } catch (error: any) {
        await showDialog(
          "error",
          error?.message ?? "저장 중 오류가 발생했습니다."
        );
        return false;
      }
    };

    // grid1의 width resize
    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };

    useImperativeHandle(ref, () => ({
      getSelectedValue: async () => {
        const ok = await onSave();
        return { excelUpload: !!ok };
      },
    }));

    const isEmptyRow = (row: any): boolean => {
      const checkFields = ["no_level", "cd_matl"];
      return checkFields.every((field) => !row[field]);
    };

    // ----------------------------------------------------------------------
    // Validation(): BOM 엑셀 업로드 데이터 전처리 + 구조 검증
    // ----------------------------------------------------------------------
    const Validation = async (): Promise<boolean> => {
      const api = gridref1.current?.api;
      if (!api) return false;
      api.stopEditing();
      const setErr = (node: any, msg: string) => {
        const prev = node.data?.error_msg
          ? String(node.data.error_msg) + " | "
          : "";
        api.getRowNode(node.id)?.setDataValue("error_msg", prev + msg);
      };

      const lvl = (r: any) => Number(r.no_level ?? 0);
      const isBlank = (v: any) =>
        v === undefined || v === null || String(v).trim() === "";

      // 표시된 모든 행 수집
      const nodes: any[] = [];
      api.forEachNode((n: any) => {
        // 빈행이면 유효성 체크 건너뜀
        if (n?.data && !isEmptyRow(n.data)) {
          api.getRowNode(n.id)?.setDataValue("error_msg", ""); // 오류 초기화
          nodes.push(n);
        }
      });

      if (nodes.length === 0) {
        await showDialog("alter", "검증할 데이터가 없습니다.");
        return false;
      }

      let hasError = false;

      // ----------------------------------------------------------------------
      // 기준 코드 데이터 로드
      // ----------------------------------------------------------------------
      const tpBomList = await getCodeData("PRD002", ""); // BOM 유형 코드
      const masItemList = await getCodeData("mas_item", ""); // 품목 마스터 코드
      const opComboList = cdOpList ?? [];

      const tpBomKeys = new Set([
        ...tpBomList.map((x: any) => norm(x.code)),
        ...tpBomList.map((x: any) => norm(x.name)),
      ]);

      const opKeys = new Set([
        ...opComboList.map((x: any) => norm(x.code)),
        ...opComboList.map((x: any) => norm(x.name)),
      ]);

      const validItemCodes = new Set(
        masItemList.map((x: any) => String(x.cd_item ?? ""))
      );

      // ----------------------------------------------------------------------
      // 0 전처리 (기본값/형식/유효성 검사)
      // ----------------------------------------------------------------------
      const dateRegex = /^\d{8}$/;

      for (const node of nodes) {
        const row = node.data;
        const id = node.id;

        // --- 날짜 필드 체크 ---------------------------------------------------
        if (!isBlank(row.dt_start) && !dateRegex.test(String(row.dt_start))) {
          setErr(node, "시작일 형식 오류(yyyyMMdd)");
          hasError = true;
        }
        if (!isBlank(row.dt_end) && !dateRegex.test(String(row.dt_end))) {
          setErr(node, "종료일 형식 오류(yyyyMMdd)");
          hasError = true;
        }

        // --- tp_bom 유효성 검사 ---------------------------------------------
        if (!isBlank(row.tp_bom) && !tpBomKeys.has(norm(row.tp_bom))) {
          setErr(node, `BOM유형(${row.tp_bom})이 유효하지 않습니다.`);
          hasError = true;
        }
        if (!validItemCodes.has(String(row.cd_matl))) {
          setErr(
            node,
            `품목 코드(${row.cd_matl})가 품목 마스터에 존재하지 않습니다.`
          );
          hasError = true;
        }

        // --- 숫자 필드(rt_loss, qt_just, qt_need) ---------------------------
        const numericFields = ["rt_loss", "qt_just", "qt_need"];
        for (const key of numericFields) {
          const val = row[key];
          if (!isBlank(val) && isNaN(Number(val))) {
            setErr(node, `${key} 값이 숫자가 아닙니다.`);
            hasError = true;
          }
        }

        // --- 공정 코드(cd_op) 유효성 ----------------------------------------
        if (!isBlank(row.cd_op) && !opKeys.has(norm(row.cd_op))) {
          setErr(node, `공정코드(${row.cd_op})가 유효하지 않습니다.`);
          hasError = true;
        }
      }

      // ----------------------------------------------------------------------
      // 1 레벨 기반 부모 찾기 (불규칙한 순서 가능)
      // ----------------------------------------------------------------------
      const lastAtLevel = new Map<number, any>(); // 각 레벨별 마지막 노드
      const childrenMap = new Map<string, string[]>(); // 부모-자식 관계
      const nodeById: Record<string, any> = {};

      for (const node of nodes) {
        const row = node.data;
        const cur = lvl(row);
        const id = String(node.id);
        nodeById[id] = node;

        if (cur === 0) {
          lastAtLevel.set(0, node);
        } else {
          // 상단에서 가장 가까운 (cur-1) 레벨 부모 찾기
          let parent: any = null;
          for (let up = cur - 1; up >= 0; up--) {
            if (lastAtLevel.has(up)) {
              parent = lastAtLevel.get(up);
              break;
            }
          }

          if (!parent) {
            setErr(
              node,
              `레벨 ${cur}의 부모(레벨 ${cur - 1})가 상단에 없습니다.`
            );
            hasError = true;
          } else {
            const parentId = String(parent.id);
            if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
            childrenMap.get(parentId)!.push(id);

            // 부모 cd_matl → 자식 cd_item 자동 세팅
            const parentMatl = parent.data?.cd_matl ?? "";
            if (isBlank(row.cd_item) || row.cd_item !== parentMatl) {
              api.getRowNode(node.id)?.setDataValue("cd_item", parentMatl);
            }
          }
        }

        // 현재 레벨 업데이트 및 더 깊은 레벨 캐시 제거
        lastAtLevel.set(cur, node);
        for (const k of Array.from(lastAtLevel.keys())) {
          if (k > cur) lastAtLevel.delete(k);
        }
      }

      // ----------------------------------------------------------------------
      // 2 동일 부모-동일 레벨 내 자품목 중복 체크
      // ----------------------------------------------------------------------
      childrenMap.forEach((kids, pid) => {
        const seen = new Set<string>();
        kids.forEach((cid) => {
          const child = nodeById[cid];
          const key = `${pid}|${child.data.cd_matl ?? ""}`;
          if (seen.has(key)) {
            setErr(child, "동일 부모 아래에 동일 자품목이 중복되었습니다.");
            hasError = true;
          }
          seen.add(key);
        });
      });

      // ----------------------------------------------------------------------
      // 3 공용자재 BOM 구조 일관성 체크
      // ----------------------------------------------------------------------
      const memoSig = new Map<string, string>();
      const buildSig = (nid: string): string => {
        if (memoSig.has(nid)) return memoSig.get(nid)!;
        const node = nodeById[nid];
        if (!node) return "";
        const r = node.data;
        const kids = (childrenMap.get(nid) ?? []).map((cid) => nodeById[cid]);
        kids.sort((a, b) =>
          (a.data.cd_matl ?? "").localeCompare(b.data.cd_matl ?? "")
        );
        const kidsSig = kids.map((k) => ({
          cd_matl: k.data.cd_matl ?? "",
          sig: buildSig(String(k.id)),
        }));
        const sig = JSON.stringify({ self: r.cd_matl ?? "", kids: kidsSig });
        memoSig.set(nid, sig);
        return sig;
      };

      const firstSigByMatl = new Map<string, { sig: string; nid: string }>();
      for (const id of Object.keys(nodeById)) {
        const node = nodeById[id];
        const matl = node.data?.cd_matl ?? "";
        const hasChildren = (childrenMap.get(id) ?? []).length > 0;
        if (!hasChildren) continue;
        const sig = buildSig(id);
        if (!firstSigByMatl.has(matl)) {
          firstSigByMatl.set(matl, { sig, nid: id });
        } else {
          const base = firstSigByMatl.get(matl)!;
          if (base.sig !== sig) {
            setErr(node, `공용자재 구조 불일치 (${matl})`);
            setErr(nodeById[base.nid], `공용자재 구조 불일치 (${matl})`);
            hasError = true;
          }
        }
      }

      // ----------------------------------------------------------------------
      // 4 항번(no_line) 채번 (부모별, 엑셀에 붙여넣은 순서 그대로)
      // 한글 주석: childrenMap은 forEachNode 순서(=원본 행 순서)로 쌓이므로 정렬하지 않는다.
      // ----------------------------------------------------------------------
      childrenMap.forEach((kids) => {
        kids.forEach((cid, idx) => {
          api.getRowNode(nodeById[cid].id)?.setDataValue("no_line", idx + 1);
        });
      });

      validationCacheRef.current.tpBomList = tpBomList;

      // ----------------------------------------------------------------------
      // 5 결과 처리
      // ----------------------------------------------------------------------
      if (hasError) {
        await showDialog(
          "error",
          "오류가 있습니다. 각 행의 오류 메시지를 확인하세요."
        );
        return false;
      } else {
        return true;
      }
    };

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_Init") {
        gridref1.current.resetGridData();
      }

      if (id == "aButton_Validation") {
        const isValid = await Validation();
        if (isValid) {
          await showDialog("alter", "모든 데이터 검증이 완료되었습니다.");
        }
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={grid1Width}
            minWidth={130}
            onResizeStop={onResizeStopH}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: grid1Width }}
          >
            <SnSheet
              ref={gridref1}
              enableAddRow={true}
              columnConfig={columns}
              rowData={dataSource1}
              isEditable={true} // 수정 가능여부
              title={Menuprops.nm_menu}
              header={
                <div>
                  <SnButton
                    text={"초기화"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Init");
                    }}
                  />
                  <SnButton
                    text={"오류체크"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Validation");
                    }}
                  />

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={onDelete}
                      sx={{
                        color: "gray",
                        "&:hover": {
                          color: "blue",
                          transform: "scale(1.2)",
                          transition: "all 0.3s ease",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              }
              codeDataList={[]}
              compareFields={[]}
            />
          </SnResizableLNR>
        </div>
      </div>
    );
  }
);

export default SN_PRD_BAS_REG_044_POP_001;
